/**
 * TeamObjectiveManagementCard Component
 *
 * Manager view - Spreadsheet-style objective management table
 * Allows creating, editing, and assigning tasks to team members
 */

import React from 'react';
import {
    Box,
    Card,
    CardContent,
    Typography,
    Button,
    IconButton,
    TextField,
    FormControl,
    Select,
    MenuItem,
    Collapse,
} from '@mui/material';
import { TrendingUp, Add, ExpandMore, Edit, Delete, Save } from '@mui/icons-material';
import { useOKRDashboardContext } from '../OKRDashboardContext';
import { EmptyState } from '../shared/EmptyState';
import { okrApi } from '../../../services/okrApi';
import { COMPLETION_STATUS_OPTIONS, getScoreFromStatus } from '../utils/scoreUtils';

// Helper to normalize status values from backend to frontend format
// Backend may store "Not Started" but frontend expects "not_started"
const normalizeStatus = (status: string | undefined | null): string => {
    if (!status) return 'not_started';
    // Convert to lowercase and replace spaces with underscores
    const normalized = status.toLowerCase().replace(/\s+/g, '_');
    // Map common variations
    const statusMap: Record<string, string> = {
        'not_started': 'not_started',
        'in_progress': 'less_than_25',
        'less_than_25': 'less_than_25',
        'less_than_50': 'less_than_50',
        'almost_75': 'almost_75',
        'almost_100': 'almost_100',
        'completed': 'completed',
        'complete': 'completed',
        '100%_complete': 'completed',
    };
    return statusMap[normalized] || 'not_started';
};

export const TeamObjectiveManagementCard: React.FC = () => {
    const { isDarkMode, data, permissions, sections, dialogs, tasks, filteredObjectives } = useOKRDashboardContext();
    const { employees, userContext, isLocked, setSuccess, setError, refetchData, softRefetchData } = data;
    const { expandedSections, toggleSection } = sections;
    const { taskData, setTaskData, newTaskRows, setNewTaskRows } = tasks;

    // Use dept-filtered objectives for manager sections (Admin+Manager sees only their dept)
    const teamObjs = filteredObjectives.myDeptTeam ?? filteredObjectives.team;

    // Only show for managers
    if (!permissions.isManager && !permissions.isAdmin) {
        return null;
    }

    const handleEditTeamObjective = (obj: any) => {
        dialogs.openEditTeamObjDialog(obj);
    };

    const handleDeleteTask = async (taskId: string) => {
        if (!window.confirm('Are you sure you want to delete this task?')) return;
        try {
            await okrApi.deleteObjective(taskId, 'individual');
            setSuccess('Task deleted successfully');
            softRefetchData();
        } catch (err: any) {
            setError(err.message || 'Failed to delete task');
        }
    };

    const handleAssignTask = async (
        objId: string,
        taskKey: string,
        selectedEmployeeId: string,
        taskTitle: string,
        taskId: string
    ) => {
        if (!selectedEmployeeId) return;

        try {
            const employeeName = employees.find(emp => emp.id === selectedEmployeeId)?.name || 'Employee';
            await okrApi.assignTaskToEmployee(objId, selectedEmployeeId, taskTitle);
            setSuccess(`Task "${taskTitle}" assigned to ${employeeName}!`);

            // Remove the assigned new-task row so it doesn't duplicate with server data
            setNewTaskRows((prev: any) => ({
                ...prev,
                [objId]: (prev[objId] || []).filter((t: any) => t.id !== taskId)
            }));

            softRefetchData();
        } catch (err: any) {
            setError(err.message || 'Failed to assign task');
        }
    };

    // Handler to save individual objective fields on blur
    const handleTaskFieldBlur = async (taskId: string, taskKey: string, task: any) => {
        // Skip for new/placeholder tasks
        if (!taskId || taskId.startsWith('new-') || taskId.startsWith('placeholder-') || task.isNew || task.isPlaceholder) {
            return;
        }

        const taskState = taskData[taskKey] || {};
        const title = taskState.title !== undefined ? taskState.title : (task.title || '');
        const startDate = taskState.startDate || (task.start_date ? task.start_date.split('T')[0] : undefined);
        const dueDate = taskState.dueDate || (task.due_date ? task.due_date.split('T')[0] : undefined);

        try {
            await okrApi.updateIndividualObjective(taskId, title, startDate, dueDate);
            setSuccess('Changes saved');
        } catch (err: any) {
            setError(err.message || 'Failed to save changes');
        }
    };

    const handleStatusChange = async (
        taskId: string,
        taskKey: string,
        newStatus: string,
        isNew: boolean
    ) => {
        const calculatedScore = getScoreFromStatus(newStatus);
        setTaskData((prev: any) => ({
            ...prev,
            [taskKey]: { ...prev[taskKey], status: newStatus, score: calculatedScore }
        }));

        if (taskId && !taskId.startsWith('task-') && !taskId.startsWith('new-') && !isNew) {
            try {
                await okrApi.updateObjectiveStatusAndScore(taskId, 'individual', newStatus, calculatedScore);
                setSuccess('Status updated successfully');
                // Soft refresh to update org/team scores without showing loading spinner
                softRefetchData();
            } catch (err: any) {
                setError(err.message || 'Failed to update status');
            }
        }
    };

    const addNewTaskRow = (objId: string) => {
        const newTaskId = `new-${objId}-${Date.now()}`;
        setNewTaskRows((prev: any) => ({
            ...prev,
            [objId]: [
                ...(prev[objId] || []),
                { id: newTaskId, title: '', isNew: true, teamObjId: objId }
            ]
        }));
    };

    // Handler to save team objective comment on blur
    const handleCommentBlur = async (objId: string, obj: any) => {
        const commentState = taskData[`${objId}-comment`];
        const comment = commentState?.selfComment;

        // Only save if comment has changed from original
        if (comment === undefined || comment === (obj.comment || '')) {
            return;
        }

        try {
            await okrApi.updateTeamObjective(objId, obj.title, undefined, undefined, comment);
            setSuccess('Comment saved');
        } catch (err: any) {
            setError(err.message || 'Failed to save comment');
        }
    };

    return (
        <Card
            sx={{
                bgcolor: isDarkMode ? 'rgba(255,255,255,0.02)' : '#fff',
                borderRadius: 4,
                boxShadow: isDarkMode
                    ? '0 25px 50px -12px rgba(0, 0, 0, 0.5)'
                    : '0 25px 50px -12px rgba(0, 0, 0, 0.1)',
                border: '1px solid',
                borderColor: isDarkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)',
            }}
        >
            <CardContent sx={{ p: 0 }}>
                {/* Header */}
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        cursor: 'pointer',
                        p: 4,
                        borderBottom: expandedSections.teamObjectiveManagement ? '1px solid' : 'none',
                        borderColor: isDarkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)',
                    }}
                    onClick={() => toggleSection('teamObjectiveManagement')}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                        <Box
                            sx={{
                                width: 56,
                                height: 56,
                                borderRadius: 3,
                                background: 'linear-gradient(135deg, rgba(6, 182, 212, 0.15) 0%, rgba(59, 130, 246, 0.25) 100%)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                border: '2px solid rgba(6, 182, 212, 0.2)',
                            }}
                        >
                            <TrendingUp sx={{ color: '#06b6d4', fontSize: 28 }} />
                        </Box>
                        <Box>
                            <Typography
                                sx={{
                                    fontWeight: 900,
                                    fontSize: '1.25rem',
                                    letterSpacing: '-0.02em',
                                }}
                            >
                                Team Objective Management
                            </Typography>
                            <Typography
                                sx={{
                                    color: isDarkMode ? '#94a3b8' : '#64748b',
                                    fontWeight: 600,
                                    fontSize: '0.875rem',
                                    mt: 0.5,
                                }}
                            >
                                Create and manage your team's objectives
                            </Typography>
                        </Box>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        {!isLocked && permissions.canCreateTeamOKR && (
                            <Button
                                variant="contained"
                                startIcon={<Add />}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    dialogs.openCreateTeamObjDialog(userContext.dept_id ?? 0);
                                }}
                                sx={{
                                    borderRadius: 2.5,
                                    background: 'linear-gradient(135deg, #06b6d4 0%, #3b82f6 100%)',
                                    boxShadow: '0 4px 12px rgba(6, 182, 212, 0.3)',
                                    fontWeight: 700,
                                    px: 3,
                                    textTransform: 'none',
                                    '&:hover': {
                                        boxShadow: '0 8px 20px rgba(6, 182, 212, 0.4)',
                                        transform: 'translateY(-2px)',
                                    },
                                    transition: 'all 0.3s ease',
                                }}
                            >
                                Create Team Objective
                            </Button>
                        )}
                        <IconButton
                            sx={{
                                bgcolor: isDarkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)',
                                width: 44,
                                height: 44,
                                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                transform: expandedSections.teamObjectiveManagement ? 'rotate(180deg)' : 'rotate(0deg)',
                            }}
                        >
                            <ExpandMore />
                        </IconButton>
                    </Box>
                </Box>

                <Collapse in={expandedSections.teamObjectiveManagement}>
                    <Box sx={{ p: 3 }}>
                        {teamObjs.length === 0 ? (
                            <EmptyState
                                icon={<TrendingUp sx={{ fontSize: 48, color: isDarkMode ? '#64748b' : '#94a3b8' }} />}
                                title="No team objectives yet"
                                description="Create your first team objective to start managing tasks"
                                actionLabel="Create Your First Team Objective"
                                onAction={() => dialogs.openCreateTeamObjDialog(userContext.dept_id || 0)}
                                isDarkMode={isDarkMode}
                            />
                        ) : (
                            <Box
                                sx={{
                                    border: '1px solid',
                                    borderColor: isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)',
                                    borderRadius: 2,
                                    overflow: 'auto',
                                }}
                            >
                                {/* Table Header */}
                                <Box
                                    sx={{
                                        display: 'grid',
                                        gridTemplateColumns: '140px 200px 100px 100px 150px 140px 120px 180px 50px',
                                        bgcolor: isDarkMode ? 'rgba(59, 130, 246, 0.15)' : '#dbeafe',
                                        borderBottom: '2px solid',
                                        borderColor: isDarkMode ? 'rgba(59, 130, 246, 0.3)' : '#3b82f6',
                                        minWidth: 1180,
                                    }}
                                >
                                    {['Objective', 'Tasks', 'Start', 'Due', 'Assign to', 'Status', 'Notes', 'Defined Measure', '+'].map((header, idx) => (
                                        <Typography
                                            key={header}
                                            sx={{
                                                p: 1,
                                                fontWeight: 700,
                                                fontSize: '0.7rem',
                                                color: isDarkMode ? '#93c5fd' : '#1d4ed8',
                                                borderRight: idx < 8 ? '1px solid' : 'none',
                                                borderColor: isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)',
                                                textAlign: idx === 8 ? 'center' : 'left',
                                            }}
                                        >
                                            {header}
                                        </Typography>
                                    ))}
                                </Box>

                                {/* Table Body */}
                                {teamObjs.map((obj: any, objIndex: number) => {
                                    // Use individualObjectives from getOrgOkrs response
                                    // (NOT milestones - those are different)
                                    let tasksList = obj.individualObjectives && obj.individualObjectives.length > 0
                                        ? obj.individualObjectives.map((io: any) => ({
                                            ...io,
                                            assigned_to: io.employee_id,  // Map for consistency
                                        }))
                                        : [];

                                    const objNewTasks = newTaskRows[obj.id] || [];
                                    tasksList = [...tasksList, ...objNewTasks];

                                    // Filter for employees - only show assigned tasks
                                    if (!permissions.isManager && !permissions.isAdmin && !permissions.isHR) {
                                        tasksList = tasksList.filter((t: any) => t.assigned_to === userContext.id || t.isNew);
                                    }

                                    // If no tasks, show a placeholder row for managers to add tasks
                                    const hasNoTasks = tasksList.length === 0;
                                    if (hasNoTasks && (permissions.isManager || permissions.isAdmin)) {
                                        tasksList = [{ id: `placeholder-${obj.id}`, title: '', isPlaceholder: true }];
                                    }

                                    return (
                                        <Box key={obj.id}>
                                            {tasksList.map((task: any, taskIdx: number) => {
                                                const taskKey = `${obj.id}-${task.id || taskIdx}`;
                                                const taskState = taskData[taskKey] || {};

                                                return (
                                                    <Box
                                                        key={taskKey}
                                                        sx={{
                                                            display: 'grid',
                                                            gridTemplateColumns: '140px 200px 100px 100px 150px 140px 120px 180px 50px',
                                                            borderBottom: '1px solid',
                                                            borderColor: isDarkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.08)',
                                                            bgcolor: isDarkMode ? 'rgba(255,255,255,0.02)' : '#fff',
                                                            minWidth: 1180,
                                                            '&:hover': {
                                                                bgcolor: isDarkMode ? 'rgba(255,255,255,0.04)' : '#f8fafc',
                                                            },
                                                        }}
                                                    >
                                                        {/* Objective - Only show on first row */}
                                                        <Box
                                                            sx={{
                                                                p: 0.75,
                                                                borderRight: '1px solid',
                                                                borderColor: isDarkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.08)',
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                bgcolor: taskIdx === 0 ? (isDarkMode ? 'rgba(255,255,255,0.03)' : '#f8fafc') : 'transparent',
                                                            }}
                                                        >
                                                            {taskIdx === 0 && (
                                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                                    <Typography sx={{ fontWeight: 700, fontSize: '0.7rem' }}>
                                                                        {obj.title}
                                                                    </Typography>
                                                                    {permissions.isManager && (
                                                                        <IconButton
                                                                            size="small"
                                                                            onClick={() => handleEditTeamObjective(obj)}
                                                                            sx={{ p: 0.25 }}
                                                                        >
                                                                            <Edit sx={{ fontSize: 12 }} />
                                                                        </IconButton>
                                                                    )}
                                                                </Box>
                                                            )}
                                                        </Box>

                                                        {/* Task Name */}
                                                        <Box
                                                            sx={{
                                                                p: 1,
                                                                borderRight: '1px solid',
                                                                borderColor: isDarkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.08)',
                                                            }}
                                                        >
                                                            <TextField
                                                                size="small"
                                                                value={taskState.title !== undefined ? taskState.title : (task.title || `Task ${taskIdx + 1}`)}
                                                                onChange={(e) =>
                                                                    setTaskData((prev: any) => ({
                                                                        ...prev,
                                                                        [taskKey]: { ...prev[taskKey], title: e.target.value },
                                                                    }))
                                                                }
                                                                onBlur={() => handleTaskFieldBlur(task.id, taskKey, task)}
                                                                sx={{
                                                                    '& .MuiInputBase-input': { fontSize: '0.8rem', py: 0.5 },
                                                                    '& .MuiOutlinedInput-root': {
                                                                        bgcolor: isDarkMode ? 'rgba(255,255,255,0.05)' : '#fff',
                                                                    },
                                                                }}
                                                                fullWidth
                                                                variant="outlined"
                                                            />
                                                        </Box>

                                                        {/* Start Date */}
                                                        <Box
                                                            sx={{
                                                                p: 1,
                                                                borderRight: '1px solid',
                                                                borderColor: isDarkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.08)',
                                                            }}
                                                        >
                                                            <TextField
                                                                size="small"
                                                                type="date"
                                                                value={taskState.startDate || (task.start_date ? task.start_date.split('T')[0] : new Date().toISOString().split('T')[0])}
                                                                onChange={(e) =>
                                                                    setTaskData((prev: any) => ({
                                                                        ...prev,
                                                                        [taskKey]: { ...prev[taskKey], startDate: e.target.value },
                                                                    }))
                                                                }
                                                                onBlur={() => handleTaskFieldBlur(task.id, taskKey, task)}
                                                                sx={{
                                                                    '& .MuiInputBase-input': { fontSize: '0.75rem', py: 0.5 },
                                                                    '& .MuiOutlinedInput-root': {
                                                                        bgcolor: isDarkMode ? 'rgba(255,255,255,0.05)' : '#fff',
                                                                    },
                                                                }}
                                                                fullWidth
                                                                variant="outlined"
                                                            />
                                                        </Box>

                                                        {/* Due Date */}
                                                        <Box
                                                            sx={{
                                                                p: 1,
                                                                borderRight: '1px solid',
                                                                borderColor: isDarkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.08)',
                                                            }}
                                                        >
                                                            <TextField
                                                                size="small"
                                                                type="date"
                                                                value={taskState.dueDate || (task.due_date ? task.due_date.split('T')[0] : '')}
                                                                onChange={(e) =>
                                                                    setTaskData((prev: any) => ({
                                                                        ...prev,
                                                                        [taskKey]: { ...prev[taskKey], dueDate: e.target.value },
                                                                    }))
                                                                }
                                                                onBlur={() => handleTaskFieldBlur(task.id, taskKey, task)}
                                                                sx={{
                                                                    '& .MuiInputBase-input': { fontSize: '0.75rem', py: 0.5 },
                                                                    '& .MuiOutlinedInput-root': {
                                                                        bgcolor: isDarkMode ? 'rgba(255,255,255,0.05)' : '#fff',
                                                                    },
                                                                }}
                                                                fullWidth
                                                                variant="outlined"
                                                            />
                                                        </Box>

                                                        {/* Assigned To */}
                                                        <Box
                                                            sx={{
                                                                p: 1,
                                                                borderRight: '1px solid',
                                                                borderColor: isDarkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.08)',
                                                            }}
                                                        >
                                                            <FormControl size="small" fullWidth>
                                                                <Select
                                                                    value={taskState.assignedTo || task.assigned_to || ''}
                                                                    onChange={(e) => {
                                                                        const selectedEmployeeId = e.target.value;
                                                                        setTaskData((prev: any) => ({
                                                                            ...prev,
                                                                            [taskKey]: { ...prev[taskKey], assignedTo: selectedEmployeeId },
                                                                        }));
                                                                        if (permissions.isManager && selectedEmployeeId) {
                                                                            const taskTitle = taskState.title || task.title || `Task ${taskIdx + 1}`;
                                                                            handleAssignTask(obj.id, taskKey, selectedEmployeeId, taskTitle, task.id);
                                                                        }
                                                                    }}
                                                                    displayEmpty
                                                                    sx={{
                                                                        fontSize: '0.75rem',
                                                                        '& .MuiSelect-select': { py: 0.75 },
                                                                        bgcolor: isDarkMode ? 'rgba(255,255,255,0.05)' : '#fff',
                                                                    }}
                                                                >
                                                                    <MenuItem value="" sx={{ fontSize: '0.8rem' }}>
                                                                        <em>Select</em>
                                                                    </MenuItem>
                                                                    {permissions.isManager && (
                                                                        <MenuItem value={userContext.id} sx={{ fontSize: '0.8rem', fontWeight: 600, color: '#3b82f6' }}>
                                                                            Assign to me
                                                                        </MenuItem>
                                                                    )}
                                                                    {employees
                                                                        .filter(
                                                                            (e) =>
                                                                                !e.role_name?.toLowerCase().includes('admin') &&
                                                                                Number(e.dept_id || e.department_id) === Number(userContext.dept_id) &&
                                                                                e.id !== userContext.id
                                                                        )
                                                                        .map((emp) => (
                                                                            <MenuItem key={emp.id} value={emp.id} sx={{ fontSize: '0.8rem' }}>
                                                                                {emp.name}
                                                                            </MenuItem>
                                                                        ))}
                                                                </Select>
                                                            </FormControl>
                                                        </Box>

                                                        {/* Status */}
                                                        <Box
                                                            sx={{
                                                                p: 1,
                                                                borderRight: '1px solid',
                                                                borderColor: isDarkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.08)',
                                                            }}
                                                        >
                                                            <FormControl size="small" fullWidth>
                                                                <Select
                                                                    value={taskState.status || normalizeStatus(task.status)}
                                                                    onChange={(e) => handleStatusChange(task.id, taskKey, e.target.value, task.isNew)}
                                                                    sx={{
                                                                        fontSize: '0.7rem',
                                                                        '& .MuiSelect-select': { py: 0.75 },
                                                                        bgcolor: (COMPLETION_STATUS_OPTIONS.find((o) => o.value === (taskState.status || normalizeStatus(task.status)))?.color || '#94a3b8') + '20',
                                                                    }}
                                                                >
                                                                    {COMPLETION_STATUS_OPTIONS.map((option) => (
                                                                        <MenuItem
                                                                            key={option.value}
                                                                            value={option.value}
                                                                            sx={{
                                                                                fontSize: '0.8rem',
                                                                                '&:hover': { bgcolor: option.color + '20' },
                                                                            }}
                                                                        >
                                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                                <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: option.color }} />
                                                                                {option.label}
                                                                            </Box>
                                                                        </MenuItem>
                                                                    ))}
                                                                </Select>
                                                            </FormControl>
                                                        </Box>

                                                        {/* Notes - Per task */}
                                                        <Box
                                                            sx={{
                                                                p: 0.5,
                                                                borderRight: '1px solid',
                                                                borderColor: isDarkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.08)',
                                                            }}
                                                        >
                                                            <TextField
                                                                size="small"
                                                                value={taskState.notes ?? task.comment ?? ''}
                                                                onChange={(e) =>
                                                                    setTaskData((prev: any) => ({
                                                                        ...prev,
                                                                        [taskKey]: { ...prev[taskKey], notes: e.target.value },
                                                                    }))
                                                                }
                                                                onBlur={() => {
                                                                    const notes = taskState.notes;
                                                                    if (notes !== undefined && notes !== (task.comment || '') && task.id && !task.id.startsWith('new-') && !task.id.startsWith('placeholder-') && !task.isNew && !task.isPlaceholder) {
                                                                        okrApi.updateIndividualObjective(task.id, task.title || '', undefined, undefined, notes)
                                                                            .then(() => setSuccess('Notes saved'))
                                                                            .catch((err: any) => setError(err.message || 'Failed to save notes'));
                                                                    }
                                                                }}
                                                                placeholder="..."
                                                                sx={{
                                                                    '& .MuiInputBase-input': { fontSize: '0.65rem', py: 0.25 },
                                                                    '& .MuiOutlinedInput-root': {
                                                                        bgcolor: isDarkMode ? 'rgba(255,255,255,0.05)' : '#fff',
                                                                    },
                                                                }}
                                                                fullWidth
                                                                variant="outlined"
                                                            />
                                                        </Box>

                                                        {/* Defined Measure - Only on first row */}
                                                        <Box
                                                            sx={{
                                                                p: 0.5,
                                                                borderRight: '1px solid',
                                                                borderColor: isDarkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.08)',
                                                                bgcolor: taskIdx === 0 ? (isDarkMode ? 'rgba(255,255,255,0.03)' : '#f8fafc') : 'transparent',
                                                            }}
                                                        >
                                                            {taskIdx === 0 && (
                                                                <TextField
                                                                    size="small"
                                                                    value={taskData[`${obj.id}-measure`] || obj.defined_measure || ''}
                                                                    onChange={(e) =>
                                                                        setTaskData((prev: any) => ({
                                                                            ...prev,
                                                                            [`${obj.id}-measure`]: e.target.value,
                                                                        }))
                                                                    }
                                                                    placeholder="Enter measure..."
                                                                    multiline
                                                                    minRows={1}
                                                                    sx={{
                                                                        '& .MuiInputBase-input': { fontSize: '0.65rem', py: 0.25 },
                                                                        '& .MuiOutlinedInput-root': {
                                                                            bgcolor: isDarkMode ? 'rgba(255,255,255,0.05)' : '#fff',
                                                                        },
                                                                    }}
                                                                    fullWidth
                                                                    variant="outlined"
                                                                />
                                                            )}
                                                        </Box>

                                                        {/* Action Buttons */}
                                                        <Box
                                                            sx={{
                                                                p: 0.5,
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                gap: 0.25,
                                                            }}
                                                        >
                                                            {(permissions.isManager || permissions.isAdmin || task.employee_id === userContext.id) &&
                                                                task.id &&
                                                                !task.id.startsWith('new-') &&
                                                                !task.id.startsWith('placeholder-') &&
                                                                !task.isPlaceholder && (
                                                                    <IconButton
                                                                        size="small"
                                                                        onClick={() => handleDeleteTask(task.id)}
                                                                        sx={{
                                                                            p: 0.25,
                                                                            '&:hover': { bgcolor: 'rgba(239, 68, 68, 0.1)' },
                                                                        }}
                                                                    >
                                                                        <Delete sx={{ fontSize: 14, color: '#ef4444' }} />
                                                                    </IconButton>
                                                                )}
                                                            {taskIdx === tasksList.length - 1 && permissions.isManager && (
                                                                <IconButton
                                                                    size="small"
                                                                    onClick={() => addNewTaskRow(obj.id)}
                                                                    sx={{
                                                                        p: 0.25,
                                                                        bgcolor: isDarkMode ? 'rgba(59, 130, 246, 0.2)' : 'rgba(59, 130, 246, 0.1)',
                                                                        '&:hover': {
                                                                            bgcolor: isDarkMode ? 'rgba(59, 130, 246, 0.3)' : 'rgba(59, 130, 246, 0.2)',
                                                                        },
                                                                    }}
                                                                >
                                                                    <Add sx={{ fontSize: 14, color: '#3b82f6' }} />
                                                                </IconButton>
                                                            )}
                                                        </Box>
                                                    </Box>
                                                );
                                            })}

                                            {/* Self Comment Row */}
                                            <Box
                                                sx={{
                                                    display: 'grid',
                                                    gridTemplateColumns: '160px 1fr',
                                                    borderBottom: permissions.isManager
                                                        ? objIndex < teamObjs.length - 1
                                                            ? '3px solid'
                                                            : '1px solid'
                                                        : '1px solid',
                                                    borderColor:
                                                        permissions.isManager && objIndex < teamObjs.length - 1
                                                            ? isDarkMode
                                                                ? 'rgba(59, 130, 246, 0.3)'
                                                                : '#3b82f6'
                                                            : isDarkMode
                                                            ? 'rgba(255,255,255,0.05)'
                                                            : 'rgba(0,0,0,0.08)',
                                                    bgcolor: isDarkMode ? 'rgba(254, 243, 199, 0.1)' : '#fef9c3',
                                                    minWidth: 1200,
                                                }}
                                            >
                                                <Box
                                                    sx={{
                                                        p: 1.5,
                                                        borderRight: '1px solid',
                                                        borderColor: isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)',
                                                    }}
                                                >
                                                    <Typography sx={{ fontWeight: 700, fontSize: '0.8rem' }}>Comment (Self)</Typography>
                                                </Box>
                                                <Box sx={{ p: 1, display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                                                    <TextField
                                                        size="small"
                                                        multiline
                                                        rows={1}
                                                        value={taskData[`${obj.id}-comment`]?.selfComment ?? obj.comment ?? ''}
                                                        onChange={(e) =>
                                                            setTaskData((prev: any) => ({
                                                                ...prev,
                                                                [`${obj.id}-comment`]: {
                                                                    ...prev[`${obj.id}-comment`],
                                                                    selfComment: e.target.value,
                                                                },
                                                            }))
                                                        }
                                                        onBlur={() => handleCommentBlur(obj.id, obj)}
                                                        placeholder="Enter your self assessment comments..."
                                                        sx={{
                                                            flex: 1,
                                                            '& .MuiInputBase-input': { fontSize: '0.8rem' },
                                                            '& .MuiOutlinedInput-root': {
                                                                bgcolor: isDarkMode ? 'rgba(255,255,255,0.05)' : '#fffbeb',
                                                            },
                                                        }}
                                                        variant="outlined"
                                                    />
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => handleCommentBlur(obj.id, obj)}
                                                        sx={{
                                                            bgcolor: isDarkMode ? 'rgba(34, 197, 94, 0.2)' : '#dcfce7',
                                                            '&:hover': {
                                                                bgcolor: isDarkMode ? 'rgba(34, 197, 94, 0.3)' : '#bbf7d0',
                                                            },
                                                        }}
                                                    >
                                                        <Save sx={{ fontSize: 18, color: '#22c55e' }} />
                                                    </IconButton>
                                                </Box>
                                            </Box>
                                        </Box>
                                    );
                                })}
                            </Box>
                        )}
                    </Box>
                </Collapse>
            </CardContent>
        </Card>
    );
};

export default TeamObjectiveManagementCard;
